import {
  IDMMF,
  ISchemaInputObjectType,
  ConnectorType,
} from '@prisma/studio-types'
import { difference, flatten } from 'lodash'

import ModelStore from 'stores/model/ModelStore'
import FieldStore from 'stores/field/FieldStore'
import EnumStore from 'stores/enum/EnumStore'
import ActionStore from 'stores/action/ActionStore'
import SessionStore from 'stores/session/SessionStore'
import TabStore from 'stores/tab/TabStore'

import getModelId from 'utils/getModelId'
import getFieldId from 'utils/getFieldId'
import getEnumId from 'utils/getEnumId'

/* This store doesn't have observables. This is mostly meant to be a nice way to interact with the DMMF */
class DMMFStore {
  // DMMF InputTypes (used to determine parameters for the `where`, `select` etc arguments of a prisma client request, among other things)
  public inputObjectTypes = new Map<string, ISchemaInputObjectType>() // { <i-name>: <i> }
  public datasourceProvider!: ConnectorType // ! is fine because it will be populated in `hydrate`

  get isPostgresDatasource() {
    return this.datasourceProvider === 'postgresql'
  }

  // Add getters for other datasource providers when we actually want to do special things with them

  /**
   * Populates ModelStore, FieldStore and EnumStore with models, fields & enums from the supplied DMMF
   *
   * @param {IDMMF} dmmf
   */
  hydrate(dmmf: IDMMF, datasourceProvider: ConnectorType) {
    // No need to save models / enums from datamodel in this store because they will be stored in ModelStore etc. anyway
    dmmf.schema.inputObjectTypes.prisma.forEach(i =>
      this.inputObjectTypes.set(i.name, i)
    )
    this.datasourceProvider = datasourceProvider

    // Then, "sync" models, fields, enums etc. (create new ones, deleted removed ones from schema)
    const { models: DMMFModels, enums: DMMFEnums } = dmmf.datamodel

    // Add all models & fields from DMMF
    DMMFModels.forEach(m => {
      const createdModel = ModelStore.add({
        dbName: m.dbName,
        name: m.name,
        plural:
          dmmf!.mappings.modelOperations.find(mm => mm.model === m.name)
            ?.plural || m.name, // While the plural will most likely exist, we're being extra safe here
        fieldIds: [],
        idFieldIds: [],
        uniqueFieldIds: [],
      })

      const fieldIds = m.fields
        .filter(f => f.kind !== 'unsupported')
        .map(
          f =>
            FieldStore.add({
              id: getFieldId(createdModel.id, f.name),
              modelId: createdModel.id,
              name: f.name,
              type: f.type,
              kind: f.kind,
              default: f.default,
              isId: f.isId,
              isUnique: f.isUnique,
              isRequired: f.isRequired,
              isList: f.isList,
              isReadOnly: f.isReadOnly,
              isUpdatedAt: f.isUpdatedAt,
              relationName: f.relationName || '',
              relationFromFieldNames: f.relationFromFields || [],
              relationToFieldNames: f.relationToFields || [],
            }).id
        )

      const idFieldIds = m.idFields.map(fieldName =>
        getFieldId(createdModel.id, fieldName)
      )

      let uniqueFieldIds: string[] = []
      if (m.uniqueFields.length > 0) {
        uniqueFieldIds = m.uniqueFields[0].map(fieldName =>
          getFieldId(createdModel.id, fieldName)
        )
      }

      createdModel.update({
        fieldIds,
        idFieldIds,
        uniqueFieldIds,
      })
    })

    // Add all enums from DMMF
    DMMFEnums.forEach(e => {
      EnumStore.add({
        name: e.name,
        values: e.values.map(v => v.name),
      })
    })
  }

  /**
   * Removes models that exist in ModelStore, but not in the supplied DMMF
   *
   * @param {IDMMF} dmmf
   */
  removeUnusedModels = (dmmf: IDMMF) => {
    const { models: DMMFModels } = dmmf.datamodel

    const modelIdsToRemove = difference(
      Object.values(ModelStore.values).map(m => m.id),
      DMMFModels.map(m => getModelId(m.name))
    )

    modelIdsToRemove.forEach(mId => {
      // Remove any actions on these models
      Object.values(ActionStore.values).forEach(action => {
        if (action.session?.isScript && action.session.script.modelId === mId) {
          ActionStore.remove(action.id)
        }
      })

      // Then, close any tabs (and sessions by extension) that these models might be open in
      Object.values(TabStore.values).forEach(tab => {
        if (tab.session?.isScript && tab.session.script.modelId === mId) {
          TabStore.remove(tab.id)
        }
      })

      // Also remove any other sessions that this model might be saved in (these are orphaned sessions that ideally shouldn't even exist, but we're doing this to be sure)
      Object.values(SessionStore.values).forEach(session => {
        if (session?.isScript && session.script.modelId === mId) {
          SessionStore.remove(session.id)
        }
      })

      // Ideally, we'd like to remove any records with these models as well, but they're only in memory, and looping over all existing records might be very expensive.

      // And then finally, remove the model
      ModelStore.remove(mId)
    })
  }

  /**
   * Removes fields that exist in FieldStore, but not in the supplied DMMF
   *
   * @param {IDMMF} dmmf
   */
  removeUnusedFields = (dmmf: IDMMF) => {
    const { models: DMMFModels } = dmmf.datamodel

    const fieldIdsToRemove = difference(
      Object.values(FieldStore.values).map(f => f.id),
      flatten(
        DMMFModels.map(m =>
          m.fields.map(f => getFieldId(getModelId(m.name), f.name))
        )
      )
    )

    fieldIdsToRemove.forEach(fId => FieldStore.remove(fId))
  }

  /**
   * Removes enums that exist in EnumStore, but not in the supplied DMMF
   *
   * @param {IDMMF} dmmf
   */
  removeUnusedEnums = (dmmf: IDMMF) => {
    const { enums: DMMFEnums } = dmmf.datamodel

    const enumIdsToRemove = difference(
      Object.values(EnumStore.values).map(e => e.id),
      DMMFEnums.map(e => getEnumId(e.name))
    )

    enumIdsToRemove.forEach(eId => EnumStore.remove(eId))
  }
}

export default new DMMFStore()
